/**
 * Structured bilingual blocks for Dungeon System + Reset System guides.
 * Event / Wings / Class Change / Systems topics are spread from sibling content files.
 * Data sourced from:
 * - Server/MuServer/Data/Custom/Mix/CustomMix.xml (HeadCode 150–155)
 * - Server/MuServer/Data/Util/ResetTable.txt (+ ResetTable.cpp / Command.dat)
 * - Item.txt for official item names
 * - Event .dat / GameServer sources for event guides
 */

import guideEventsContent from "./guideEventsContent";
import guideWingsContent from "./guideWingsContent";
import guideClassChangeContent from "./guideClassChangeContent";
import guideSystemsContent from "./guideSystemsContent";

function fmtZen(n) {
  return Number(n).toLocaleString("en-US");
}

const RATES_DEFAULT = "Regular 70% · Bronze 75% · Silver 80% · Gold 85%";
const RATES_DEFAULT_HE = "Regular 70% · Bronze 75% · Silver 80% · Gold 85%";
const RATES_KANTURU = "Regular 100% · Bronze 100% · Silver 100% · Gold 100%";
const RATES_KANTURU_HE = "Regular 100% · Bronze 100% · Silver 100% · Gold 100%";

/** Per-reset Point_AL0 and Money_AL0 from ResetTable.txt section 0 and 1 */
const RESET_ROWS = [
  [1, 800, 5000000, 25000000],
  [2, 500, 10000000, 50000000],
  [3, 500, 15000000, 75000000],
  [4, 500, 20000000, 100000000],
  [5, 1000, 25000000, 125000000],
  [6, 500, 30000000, 150000000],
  [7, 500, 35000000, 175000000],
  [8, 500, 40000000, 200000000],
  [9, 500, 45000000, 225000000],
  [10, 1200, 50000000, 250000000],
  [11, 550, 82500000, 300000000],
  [12, 550, 90000000, 350000000],
  [13, 550, 97500000, 400000000],
  [14, 550, 105000000, 450000000],
  [15, 1400, 112500000, 500000000],
  [16, 550, 160000000, 600000000],
  [17, 550, 170000000, 700000000],
  [18, 550, 180000000, 800000000],
  [19, 550, 190000000, 900000000],
  [20, 1600, 200000000, 1000000000],
  [21, 600, 262500000, 1100000000],
  [22, 600, 275000000, 1200000000],
  [23, 600, 287500000, 1300000000],
  [24, 600, 300000000, 1400000000],
  [25, 1800, 312500000, 1500000000],
  [26, 600, 390000000, 1600000000],
  [27, 600, 405000000, 1700000000],
  [28, 600, 420000000, 1800000000],
  [29, 600, 435000000, 1900000000],
  [30, 2000, 450000000, 2000000000],
  [31, 650, 542500000, 2000000000],
  [32, 650, 560000000, 2000000000],
  [33, 650, 577500000, 2000000000],
  [34, 650, 595000000, 2000000000],
  [35, 2200, 612500000, 2000000000],
  [36, 650, 720000000, 2000000000],
  [37, 650, 740000000, 2000000000],
  [38, 650, 760000000, 2000000000],
  [39, 650, 780000000, 2000000000],
  [40, 2400, 800000000, 2000000000],
  [41, 700, 1025000000, 2000000000],
  [42, 700, 1050000000, 2000000000],
  [43, 700, 1075000000, 2000000000],
  [44, 700, 1100000000, 2000000000],
  [45, 2600, 1125000000, 2000000000],
  [46, 700, 1380000000, 2000000000],
  [47, 700, 1410000000, 2000000000],
  [48, 700, 1440000000, 2000000000],
  [49, 700, 1470000000, 2000000000],
  [50, 2800, 1500000000, 2000000000],
];

function recipe(cfg) {
  return {
    type: "recipe",
    headCode: cfg.headCode,
    name: cfg.name,
    dungeon: cfg.dungeon,
    result: cfg.result,
    zen: cfg.zen,
    rates: cfg.rates || RATES_DEFAULT,
    ratesHe: cfg.ratesHe || cfg.rates || RATES_DEFAULT_HE,
    failType: 0,
    materials: cfg.materials,
  };
}

export const guideSpecialContent = {
  dungeons: [
    {
      type: "p",
      en: "Mu Beginning includes a custom Dungeon catalog (NPC) with daily entries. Daily entry counters reset at 08:00 server time.",
      he: "ב־Mu Beginning יש קטלוג מבוכים מותאם (NPC) עם כניסות יומיות. מוני הכניסות מתאפסים ב־08:00 שעון השרת.",
    },
    {
      type: "p",
      en: "VIP increases daily dungeon entry caps: Normal 1 / Bronze 2 / Silver 3 / Gold 4 (where applicable). Same-IP partying inside dungeons is blocked.",
      he: "VIP מעלה את מגבלת הכניסות היומיות: Normal 1 / Bronze 2 / Silver 3 / Gold 4 (לפי הכללים). פארטי מאותה IP בתוך מבוכים חסום.",
    },
    {
      type: "p",
      en: "Each dungeon tier requires its Relic (entry key). Craft every Relic in the Chaos Machine using the recipes below. There is no upgrade chain between Relics; each key is crafted independently.",
      he: "כל דרגת מבוך דורשת את ה־Relic (מפתח הכניסה) שלה. יוצרים כל Relic ב־Chaos Machine לפי המתכונים למטה. אין שרשרת שדרוג בין Relics; כל מפתח נוצר בנפרד.",
    },
    {
      type: "p",
      en: "Most Relic mixes use VIP success rates: Regular 70% · Bronze 75% · Silver 80% · Gold 85%. Kanturu Relic is 100% for all VIP tiers. On failure, all materials in the mix are lost.",
      he: "רוב מקסי ה־Relic משתמשים בשיעורי הצלחה לפי VIP: Regular 70% · Bronze 75% · Silver 80% · Gold 85%. Kanturu Relic הוא 100% לכל דרגות ה־VIP. בכישלון כל החומרים במקס אובדים.",
    },
    {
      type: "h",
      en: "Dungeon overview",
      he: "סקירת מבוכים",
    },
    {
      type: "p",
      en: "Blood Castle Dungeon — entry key: Blood Relic · 4 tiers by reset (0–15 / 16–25 / 26–35 / 36–50) · ~15 min · up to 10/day · high EXP.",
      he: "Blood Castle Dungeon — מפתח: Blood Relic · 4 דרגות לפי ריסט (0–15 / 16–25 / 26–35 / 36–50) · ~15 דק' · עד 10/יום · EXP גבוה.",
    },
    {
      type: "p",
      en: "Chaos Castle Dungeon — Chaos Relic · 4 tiers · ~10 min · 10/day · jewel packs (Chaos / Bless / Soul / Life / Creation / Guardian).",
      he: "Chaos Castle Dungeon — Chaos Relic · 4 דרגות · ~10 דק' · 10/יום · חבילות תכשיטים (Chaos / Bless / Soul / Life / Creation / Guardian).",
    },
    {
      type: "p",
      en: "Devil Square Dungeon — Devil Relic · 4 tiers · ~20 min · 10/day · Gemstone, Jewel of Harmony, Lower Refine Stone pools.",
      he: "Devil Square Dungeon — Devil Relic · 4 דרגות · ~20 דק' · 10/יום · Gemstone, Jewel of Harmony, Lower Refine Stone.",
    },
    {
      type: "p",
      en: "Imperial Guardian — Imperial Relic · difficulties 0–3 · ~55 min · 10/day · Master experience enabled.",
      he: "Imperial Guardian — Imperial Relic · דרגות קושי 0–3 · ~55 דק' · 10/יום · Master EXP מופעל.",
    },
    {
      type: "p",
      en: "Kanturu Dungeon (Battle of Maya) — Kanturu Relic · T1–T4 · ~45 min · 5/day · Fenrir materials (Splinter of Armor, Bless of Guardian, Claw of Beast).",
      he: "Kanturu Dungeon (Battle of Maya) — Kanturu Relic · T1–T4 · ~45 דק' · 5/יום · חומרי Fenrir (Splinter of Armor, Bless of Guardian, Claw of Beast).",
    },
    {
      type: "p",
      en: "Nightmare Dungeon (Battle of Nightmare) — Nightmare Relic · T1–T4 · ~20 min · 10/day · EXP + Beginning Boxes.",
      he: "Nightmare Dungeon (Battle of Nightmare) — Nightmare Relic · T1–T4 · ~20 דק' · 10/יום · EXP + Beginning Boxes.",
    },
    {
      type: "h",
      en: "How to craft Dungeon Relics",
      he: "איך ליצור Dungeon Relics",
    },
    {
      type: "p",
      en: "Open the Chaos Machine, select the Relic mix, place exactly the required materials, pay the Zen cost, and combine. Bundled jewels can substitute loose jewels where the mix allows (same total jewel count).",
      he: "פתחו את ה־Chaos Machine, בחרו את מקס ה־Relic, שימו בדיוק את החומרים הנדרשים, שלמו את עלות ה־Zen ושלבו. חבילות תכשיטים יכולות להחליף תכשיטים בודדים כשהמקס מאפשר (אותו סה״כ תכשיטים).",
    },

    recipe({
      headCode: 150,
      name: { en: "Blood Relic", he: "Blood Relic" },
      dungeon: {
        en: "Blood Castle Dungeon key",
        he: "מפתח Blood Castle Dungeon",
      },
      result: "Blood Relic",
      zen: 7500000,
      materials: [
        { en: "Excellent Item +0 ×1", he: "Excellent Item +0 ×1" },
        { en: "Item +9 ×1", he: "Item +9 ×1" },
        { en: "Jewel of Chaos ×1", he: "Jewel of Chaos ×1" },
      ],
    }),

    recipe({
      headCode: 151,
      name: { en: "Chaos Relic", he: "Chaos Relic" },
      dungeon: {
        en: "Chaos Castle Dungeon key",
        he: "מפתח Chaos Castle Dungeon",
      },
      result: "Chaos Relic",
      zen: 30000000,
      materials: [
        {
          en: "Excellent Item +4 + Additional Option ×1",
          he: "Excellent Item +4 + Additional Option ×1",
        },
        { en: "Item +9 ×1", he: "Item +9 ×1" },
        { en: "Jewel of Bless ×1", he: "Jewel of Bless ×1" },
        { en: "Jewel of Soul ×1", he: "Jewel of Soul ×1" },
        { en: "Jewel of Chaos ×1", he: "Jewel of Chaos ×1" },
      ],
    }),

    recipe({
      headCode: 152,
      name: { en: "Devil Relic", he: "Devil Relic" },
      dungeon: {
        en: "Devil Square Dungeon key",
        he: "מפתח Devil Square Dungeon",
      },
      result: "Devil Relic",
      zen: 30000000,
      materials: [
        {
          en: "Excellent Item +4 + Additional Option ×2",
          he: "Excellent Item +4 + Additional Option ×2",
        },
        { en: "Item +9 ×1", he: "Item +9 ×1" },
        { en: "Jewel of Bless ×1", he: "Jewel of Bless ×1" },
        { en: "Jewel of Soul ×1", he: "Jewel of Soul ×1" },
        { en: "Jewel of Chaos ×1", he: "Jewel of Chaos ×1" },
      ],
    }),

    recipe({
      headCode: 153,
      name: { en: "Imperial Relic", he: "Imperial Relic" },
      dungeon: { en: "Imperial Guardian key", he: "מפתח Imperial Guardian" },
      result: "Imperial Relic",
      zen: 50000000,
      materials: [
        { en: "Excellent Item +0 ×4", he: "Excellent Item +0 ×4" },
        { en: "Item +9 ×1", he: "Item +9 ×1" },
        { en: "Jewel of Bless ×1", he: "Jewel of Bless ×1" },
        { en: "Jewel of Soul ×1", he: "Jewel of Soul ×1" },
        { en: "Jewel of Chaos ×1", he: "Jewel of Chaos ×1" },
      ],
    }),

    recipe({
      headCode: 154,
      name: { en: "Kanturu Relic", he: "Kanturu Relic" },
      dungeon: {
        en: "Kanturu Dungeon (Battle of Maya) key",
        he: "מפתח Kanturu Dungeon (Battle of Maya)",
      },
      result: "Kanturu Relic",
      zen: 20000000,
      rates: RATES_KANTURU,
      ratesHe: RATES_KANTURU_HE,
      materials: [
        {
          en: "First Secromicon Fragment ×1",
          he: "First Secromicon Fragment ×1",
        },
        {
          en: "Second Secromicon Fragment ×1",
          he: "Second Secromicon Fragment ×1",
        },
        {
          en: "Third Secromicon Fragment ×1",
          he: "Third Secromicon Fragment ×1",
        },
        {
          en: "Fourth Secromicon Fragment ×1",
          he: "Fourth Secromicon Fragment ×1",
        },
        {
          en: "Fifth Secromicon Fragment ×1",
          he: "Fifth Secromicon Fragment ×1",
        },
        {
          en: "Sixth Secromicon Fragment ×1",
          he: "Sixth Secromicon Fragment ×1",
        },

        { en: "Jewel of Chaos ×1", he: "Jewel of Chaos ×1" },
      ],
    }),

    recipe({
      headCode: 155,
      name: { en: "Nightmare Relic", he: "Nightmare Relic" },
      dungeon: {
        en: "Nightmare Dungeon (Battle of Nightmare) key",
        he: "מפתח Nightmare Dungeon (Battle of Nightmare)",
      },
      result: "Nightmare Relic",
      zen: 75000000,
      materials: [
        {
          en: "Excellent Item +9 + Additional Option ×3",
          he: "Excellent Item +9 + Additional Option ×3",
        },
        { en: "Item +11 ×1", he: "Item +11 ×1" },
        { en: "Jewel of Bless ×3", he: "Jewel of Bless ×3" },
        { en: "Jewel of Soul ×3", he: "Jewel of Soul ×3" },
        { en: "Jewel of Chaos ×2", he: "Jewel of Chaos ×2" },
      ],
    }),
  ],

  reset: [
    {
      type: "p",
      en: "Command: /reset (Auto-Reset is enabled). Required level for every reset: 400. After a successful reset you return to level 1.",
      he: "פקודה: /reset (Auto-Reset מופעל). רמה נדרשת לכל ריסט: 400. אחרי ריסט מוצלח חוזרים לרמה 1.",
    },
    {
      type: "p",
      en: "Maximum resets before Grand Reset: 50. Daily reset limit: 1 per day (account VIP does not change this cap).",
      he: "מקסימום ריסטים לפני Grand Reset: 50. מגבלת ריסט יומית: 1 ליום (VIP לא משנה את התקרה הזו).",
    },
    {
      type: "h",
      en: "How Reset points work",
      he: "איך עובדות נקודות הריסט",
    },
    {
      type: "p",
      en: "This server uses Reset Type 1. When you reset, your invested stats are wiped back to class defaults, then your available Level-Up Points are rebuilt from:",
      he: "השרת עובד ב־Reset Type 1. בריסט, הסטאטים שהשקעתם מתאפסים לברירת המחלקה, ואז נקודות ה־Level-Up הזמינות נבנות מחדש מ־:",
    },
    {
      type: "p",
      en: "1) Permanent Grand Reset points (if you already have a Grand Reset) · 2) The sum of every Reset Table point reward from Reset 1 up to your new reset count · 3) Any quest reset bonus you already earned.",
      he: "1) נקודות Grand Reset קבועות (אם כבר ביצעתם Grand Reset) · 2) סכום תגמולי הנקודות מטבלת הריסט מריסט 1 עד מונה הריסט החדש · 3) בונוס קווסט ריסט שכבר צברתם.",
    },
    {
      type: "p",
      en: "The Point value in the table below is what that specific Reset adds into the cumulative Reset Table total (not a lifetime display by itself). All VIP tiers use the same Point values on this server (Point rate = 100% for every class).",
      he: "ערך ה־Point בטבלה למטה הוא מה שאותו ריסט ספציפי מוסיף לסכום המצטבר של טבלת הריסט (לא תצוגת נקודות מצטברת בפני עצמה). כל דרגות ה־VIP משתמשות באותם ערכי Point בשרת הזה (Point rate = 100% לכל מחלקה).",
    },
    {
      type: "p",
      en: "Example: completing Reset 1 adds 800 points to the table total. Completing Reset 2 then adds another 500 (total from the table so far: 1,300), and so on.",
      he: "דוגמה: השלמת ריסט 1 מוסיפה 800 נקודות לסכום הטבלה. השלמת ריסט 2 מוסיפה עוד 500 (סה״כ מהטבלה עד כה: 1,300), וכן הלאה.",
    },
    {
      type: "p",
      en: "After a Grand Reset, your reset count returns to 0 and Section 1 of ResetTable.txt is used (higher Zen costs, same Point column pattern). Grand Reset itself is covered in the Grand Reset guide (requires 50 resets; reward 15,000 points).",
      he: "אחרי Grand Reset מונה הריסטים חוזר ל־0 ומשתמשים ב־Section 1 של ResetTable.txt (עלויות Zen גבוהות יותר, אותה תבנית נקודות). Grand Reset עצמו מפורט במדריך Grand Reset (דורש 50 ריסטים; תגמול 15,000 נקודות).",
    },
    {
      type: "h",
      en: "Full Reset table (every reset)",
      he: "טבלת ריסט מלאה (כל ריסט בנפרד)",
    },
    {
      type: "p",
      en: "Look up your next Reset number in the left column to see exactly how many points that Reset awards, and the Zen cost before / after Grand Reset.",
      he: "חפשו את מספר הריסט הבא שלכם בעמודה השמאלית כדי לראות בדיוק כמה נקודות אותו ריסט נותן, ואת עלות ה־Zen לפני / אחרי Grand Reset.",
    },
    {
      type: "table",
      columns: [
        { key: "reset", en: "Reset", he: "ריסט" },
        { key: "points", en: "Points awarded", he: "נקודות שמתקבלות" },
        { key: "zen0", en: "Zen (before GR)", he: "Zen (לפני GR)" },
        { key: "zen1", en: "Zen (after GR)", he: "Zen (אחרי GR)" },
      ],
      rows: RESET_ROWS.map(([reset, points, zen0, zen1]) => ({
        reset,
        points: points.toLocaleString("en-US"),
        zen0: fmtZen(zen0),
        zen1: fmtZen(zen1),
      })),
    },
  ],

  ...guideEventsContent,
  ...guideWingsContent,
  ...guideClassChangeContent,
  ...guideSystemsContent,
};

export default guideSpecialContent;
